import { UserRole } from '@alert-manager/lib';
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import AdminPanel from './admin/AdminPanel';
import PasswordChangeForm from './forms/PasswordChangeForm';
import Button from './ui/Button';

interface SettingsPageProps {
  onClose: () => void;
  embedded?: boolean;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ onClose, embedded = false }) => {
  const [activeTab, setActiveTab] = useState<'general' | 'admin'>('general');
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePasswordChangeSuccess = () => {
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const borderCol = isDark ? 'border-slate-700' : 'border-slate-200';

  const settingsInner = (
    <>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
          Settings
        </h2>
        {!embedded && (
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-2xl font-bold">
            <span className="material-icons" style={{ fontSize: '24px' }}>
              close
            </span>
          </button>
        )}
      </div>

      {/* Tab Navigation - Only show if admin */}
      {user?.role === UserRole.ADMIN && (
        <div className={`mb-6 border-b ${borderCol}`}>
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === 'general'
                  ? isDark
                    ? 'border-blue-400 text-blue-400'
                    : 'border-blue-600 text-blue-600'
                  : isDark
                    ? 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              General
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center ${
                activeTab === 'admin'
                  ? isDark
                    ? 'border-blue-400 text-blue-400'
                    : 'border-blue-600 text-blue-600'
                  : isDark
                    ? 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="material-icons mr-1" style={{ fontSize: '16px' }}>
                admin_panel_settings
              </span>
              Admin
            </button>
          </nav>
        </div>
      )}

      {/* Tab Content */}
      {(!user?.role || user?.role !== UserRole.ADMIN || activeTab === 'general') && (
        <div>
          <div className={`mb-6 p-4 rounded-lg ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
            <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Account Information
            </h3>
            <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              <p>
                <strong>Username:</strong> {user?.username}
              </p>
              <p>
                <strong>Login Time:</strong> {user?.loginTime ? new Date(user.loginTime).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>

          <PasswordChangeForm onSuccess={handlePasswordChangeSuccess} />

          {!embedded && (
            <div className="flex justify-end mt-4">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      )}

      {user?.role === UserRole.ADMIN && activeTab === 'admin' && <AdminPanel />}
    </>
  );

  if (embedded) {
    return (
      <div className="flex-1 overflow-auto p-6" style={{ backgroundColor: 'var(--surface)' }}>
        <div className={`max-w-4xl mx-auto ${isDark ? 'bg-slate-900' : 'bg-white'} rounded-xl p-6 border ${borderCol}`}>{settingsInner}</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleBackdropClick}>
      <div
        className={`rounded-xl shadow-lg p-6 overflow-y-auto ${
          user?.role === UserRole.ADMIN ? 'max-w-4xl h-[85vh]' : 'max-w-md h-[85vh]'
        } w-full ${isDark ? 'bg-slate-900' : 'bg-white'}`}
      >
        {settingsInner}
      </div>
    </div>
  );
};

export default SettingsPage;
